import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { logout } from '../../actions/auth';
import { refreshSession } from '../../util/api';
import { IDLE_PROMPT_MS, IDLE_TIMEOUT_MS } from '../../actions/constants';

// Web-only inactivity guard. After IDLE_TIMEOUT_MS of no interaction we show a
// warning modal counting down IDLE_PROMPT_MS, then log the user out. This is a
// UX layer on top of the short-lived access token / refresh flow; the real
// session enforcement still lives server-side in the token lifetimes.
const IdleTimeoutHandler = () => {
  const dispatch = useDispatch();
  const [promptOpen, setPromptOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    Math.ceil(IDLE_PROMPT_MS / 1000)
  );
  const countdownRef = useRef(null);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    clearCountdown();
    setPromptOpen(false);
    dispatch(logout('You have been logged out due to inactivity.', 'info'));
  }, [clearCountdown, dispatch]);

  const handlePrompt = useCallback(() => {
    setPromptOpen(true);
    setRemainingSeconds(Math.ceil(IDLE_PROMPT_MS / 1000));

    // Drive the visible countdown off a fixed end time so it can't drift.
    const endTime = Date.now() + IDLE_PROMPT_MS;
    clearCountdown();
    countdownRef.current = setInterval(() => {
      const msLeft = endTime - Date.now();
      setRemainingSeconds(msLeft > 0 ? Math.ceil(msLeft / 1000) : 0);
      if (msLeft <= 0) {
        clearCountdown();
      }
    }, 250);
  }, [clearCountdown]);

  const { activate } = useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    promptBeforeIdle: IDLE_PROMPT_MS,
    onPrompt: handlePrompt,
    onIdle: handleLogout,
    crossTab: true,
    syncTimers: 200,
  });

  const handleStay = useCallback(async () => {
    clearCountdown();
    setPromptOpen(false);
    activate(); // reset the idle timer (broadcast across tabs)

    try {
      // Proactively refresh so the user's next request doesn't 401-then-retry.
      await refreshSession();
    } catch {
      // Refresh token expired / blacklisted — the session can't be extended.
      handleLogout();
    }
  }, [activate, clearCountdown, handleLogout]);

  useEffect(() => clearCountdown, [clearCountdown]);

  return (
    <Dialog open={promptOpen} onClose={handleStay} aria-labelledby="idle-timeout-title">
      <DialogTitle id="idle-timeout-title">Are you still there?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You will be logged out in {remainingSeconds} second
          {remainingSeconds === 1 ? '' : 's'} due to inactivity.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="inherit">
          Log out
        </Button>
        <Button onClick={handleStay} variant="contained" autoFocus>
          Stay logged in
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IdleTimeoutHandler;
